/* eslint-disable eqeqeq */
import { Form, Formik, getIn } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import ConfirmSelect from '../../../components/confirm-select';
import ConfirmWithReason from '../../../components/confirm-with-reason';
import ConfirmWithSelectReason from '../../../components/confirm-with-select-reason';
import DisplayAddressResult from '../../../components/display-address-result';
import SaveBackdrop from '../../../components/saveBackdrop';
import Wait from '../../../components/wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  addressValidation, apiReponseError, apiRequestError, canReadEnlistment, canWriteEnlistment,
  compareArrayObjectValue, downloadEnlistmentProgramLocationTaxExemptFile, filterCountryStates,
  formatDateForDataGrid, formatDateTimeForDataGrid, getDefaultAddressResult,
  getEnlistmentProgramLocation, getErrorObject, goToRoutePage, isFormDataValid,
  isFormReferenceValid, isValidJSONString
} from '../../../utils/helper';
import {
  AddIcon, ChangeProgramIcon, ClearAllIcon, GetAppToneIcon, SearchIcon, ValidateAddressIcon
} from '../../../utils/icons';
import {
  Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormHelperText, Grid, Hidden, IconButton, Input, InputAdornment, InputLabel, LinearProgress,
  Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography
} from '../../../utils/material-ui-core';
import CreateParentAccount from './createParentAccount';
import ParentAccountSearch from './parentAccountSearch';

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean
  }
}

interface LocationKeyProps {
  partnerKey: string;
  programKey: string;
  locationItemKey: string;
}

interface ReviewProps {
  open: boolean;
  keys: LocationKeyProps;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

const formDefaultValue = {
  account: {
    accountName: "",
    accountNumber: "",
    address: {
      addressline1: "",
      addressline2: "",
      addressline3: "",
      country: "USA",
      locality1: "",
      locality2: "",
      postalCode: "",
      region1: "",
      region2: "",
    },
    group: "",
    premise: "",
  },
  adminMetadataValues: [
    {
      metadataRowGuid: "",
      name: "",
      value: "",
    },
  ],
  contact: {
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
  },
  locationItemKey: "",
  installMethodId: 0,
  kitNo: 0,
  lastModifiedBy: "",
  lastModifiedDateTime: "",
  overrideAccountAddressValidation: false,
  overrideShippingAddressValidation: false,
  programCode: 0,
  parentAccountNumber: "",
  participantAgreement: {
    date: "",
    firstName: "",
    lastName: "",
  },
  participantMetadataValues: [
    {
      metadataRowGuid: "",
      name: "",
      value: "",
    },
  ],
  reviewLocationOtherAttributes: {
    externalValidationStatus: false,
    accountGroupName: "",
    programName: "",
    partnerCode: "",
    partnerName: "",
    cancelOrderDate: "",
    cancelOrderNote: "",
    cancelOrderRefundAmount: 0,
    cancelOrderRefundDate: "",
    cancelOrderRefundStatus: false,
    enrollCompleted: "",
    errorMessage: "",
    kitName: "",
    installMethodName: "",
    isError: false,
    isParent: false,
    noofAccounts: 0,
    orderNumber: "",
    orderPaymentDate: "",
    onHold: false,
    reason: "",
    registerDateStatus: "",
    reviewCompleted: "",
    status: 0,
    statusName: "",
    withdrawDate: "",
    parentAccountName: "",
    accountNumberFormTitle: "",
    accountNameFormTitle: "",
    subscriptionBilling: false,
    partnerKey: "",
    programKey: "",
    taxExempt: false,
    taxExemptFileExists: false,
  },
  shipping: {
    address: {
      addressline1: "",
      addressline2: "",
      addressline3: "",
      country: "USA",
      locality1: "",
      locality2: "",
      postalCode: "",
      region1: "",
      region2: "",
    },
    email: "",
    firstName: "",
    lastName: "",
    organizationName: "",
    phone: "",
  },
  signupKey: "",
  slimEligible: false,
  longitude: 0.0,
  latitude: 0.0
};

const formDefaultTouchValue = {
  account: {
    accountName: true,
    accountNumber: true,
    address: {
      addressline1: true,
      addressline2: false,
      addressline3: false,
      country: true,
      locality1: true,
      locality2: false,
      postalCode: true,
      region1: true,
      region2: false,
    },
    group: true,
    premise: true,
  },
  contact: {
    email: true,
    firstName: true,
    lastName: true,
    phone: true,
  },
  installMethodId: true,
  kitNo: true,
  parentAccountNumber: true,
  shipping: {
    address: {
      addressline1: true,
      addressline2: false,
      addressline3: false,
      country: true,
      locality1: true,
      locality2: false,
      postalCode: true,
      region1: true,
      region2: false,
    },
    email: true,
    firstName: true,
    lastName: true,
    organizationName: true,
    phone: true,
  },
  signupKey: true,
  slimEligible: true,
  latitude: true,
  longitude: true
};

const formValidationSchema = Yup.object().shape({
  parentAccountNumber: Yup.string().trim().required("Parent account is required."),
  // signupKey: Yup.string().trim().required("Signup key is required."),
  installMethodId: Yup.number().required("Install method is required.").moreThan(0, "Install method not matching with our values."),
  kitNo: Yup.number().required("Kit is required.").moreThan(-2, "Kit not matching with our values."),
  account: Yup.object().shape({
    accountName: Yup.string().trim().required("Account name is required."),
    accountNumber: Yup.string().trim().required("Account number is required."),
    address: Yup.object().shape({
      addressline1: Yup.string().trim().required("Address line 1 is required."),
      locality1: Yup.string().trim().required("City is required."),
      region1: Yup.string().trim().required("State is required."),
      postalCode: Yup.string().trim().required("Postal code is required."),
      country: Yup.string().trim().required("Country is required."),
    }),
  }),
  shipping: Yup.object().shape({
    organizationName: Yup.string().trim().required("Organization name is required."),
    firstName: Yup.string().trim().required("First name is required."),
    lastName: Yup.string().trim().required("Last name is required."),
    phone: Yup.string().trim().required("Phone is required.").min(10, "Phone number is not valid.").max(10, "Phone number is not valid"),
    email: Yup.string().trim().email().required("Email is required."),
    address: Yup.object().shape({
      addressline1: Yup.string().trim().required("Address line 1 is required."),
      locality1: Yup.string().trim().required("City is required."),
      region1: Yup.string().trim().required("State is required."),
      postalCode: Yup.string().trim().required("Postal code is required."),
      country: Yup.string().trim().required("Country is required."),
    }),
  }),
  contact: Yup.object().shape({
    firstName: Yup.string().trim().required("First name is required."),
    lastName: Yup.string().trim().required("Last name is required."),
    phone: Yup.string().trim().required("Phone is required.").min(10, "Phone number is not valid.").max(10, "Phone number is not valid"),
    email: Yup.string().trim().email().required("Email is required."),
  }),
});

const defaultAddressResultOutput: AddressResultOutput = getDefaultAddressResult();

const initialAddressResultOutput: AddressResultOutput = getDefaultAddressResult();

const Review = ({ open, keys, onCallback }: ReviewProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { referenceLookups, showAlert, userFunctionAccess } = useContext(AppContext);

  const formReference = useRef(null) as any;

  const [openParentAccountSearchDialog, setOpenParentAccountSearchDialog] = useState<boolean>(false);
  const [openCreateParentAccountDialog, setOpenCreateParentAccountDialog] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [onHold, setOnHold] = useState<boolean>(false);
  const [isGroup, setIsGroup] = useState<boolean>(false);
  const [isParent, setIsParent] = useState<boolean>(false);
  const [accountAddressValidationInProgress, setAccountAddressValidationInProgress] = useState<boolean>(false);
  const [shippingAddressValidationInProgress, setShippingAddressValidationInProgress] = useState<boolean>(false);
  const [saveButtonVisible, setSaveButtonVisible] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<EnlistmentReviewAccount>(formDefaultValue);
  const [readonly, setReadonly] = useState<boolean>(false);
  const [accountAddressResultOutput, setAccountAddressResultOutput] = useState<AddressResultOutput>(initialAddressResultOutput);
  const [shippingAddressResultOutput, setShippingAddressResultOutput] = useState<AddressResultOutput>(initialAddressResultOutput);
  const [accountStates, setAccountStates] = useState<State[]>([]);
  const [shippingStates, setShippingStates] = useState<State[]>([]);
  const [locationKits, setLocationKits] = useState<LocationKit[]>([]);
  const [kitDevices, setKitDevices] = useState<KitDeviceLookupItem[]>([]);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [changePrograms, setChangePrograms] = useState<EnlistmentChangeProgram[]>([]);
  const [changeProgramDialogOpen, setChangeProgramDialogOpen] = useState<boolean>(false);
  const [changeProgramLookupValues, setChangeProgramLookupValues] = useState<Lookup[]>([]);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState<boolean>(false);
  const [cancelOrderDialogOpen, setCancelOrderDialogOpen] = useState<boolean>(false);
  const [orderCancelReasonLookupValues, setOrderCancelReasonLookupValues] = useState<Lookup[]>([]);
  const [orderCancelReasons, setOrderCancelReason] = useState<EnlistmentOrderCancelReason[]>([]);

  const [groupRegistationCloseDialogOpen, setGroupRegistationCloseDialogOpen] = useState<boolean>(false);
  const [groupRegistationCloseReasonLookupValues, setGroupRegistationCloseReasonLookupValues] = useState<Lookup[]>([]);

  const [refreshList, setRefreshList] = useState<boolean>(false);

  // Parent Account Search

  const showCreateParentAccountDialog = () => {
    setOpenCreateParentAccountDialog(true);
  }

  const confirmCreateParentAccountDialog = (confirmOutput: ConfirmOutput) => {
    setOpenCreateParentAccountDialog(false);

    if (confirmOutput.status === "refresh") {
      refresh();
    }
  }

  const showParentAccountSearchDialog = () => {
    setOpenParentAccountSearchDialog(true);
  }

  const confirmParentAccountSearchDialog = (confirmOutput: ConfirmOutput) => {
    setOpenParentAccountSearchDialog(false);

    if (confirmOutput.status === "select") {
      formReference.current.setFieldValue("parentAccountNumber", confirmOutput.data.accountNumber);
      formReference.current.setFieldValue("reviewLocationOtherAttributes.parentAccountName", confirmOutput.data.name);
      formReference.current.validateForm();
      setSaveButtonVisible(true);
    }
  }

  // Withdraw

  const showWithdrawReason = () => {
    setWithdrawDialogOpen(true);
  };

  const withdrawReasonDialogConfirmation = async (confirmWithReasonOutput: ConfirmWithReasonOutput) => {
    setWithdrawDialogOpen(false);
    if (confirmWithReasonOutput.confirm) {
      const payload = {
        reason: confirmWithReasonOutput.reason,
      };

      try {
        setSaveInProgress(true);
        await API.post("/api/enlistmentadmin/locations/" + keys.locationItemKey + "/withdraw", payload)
          .then(async (response) => {
            showAlert({ message: "Successfully saved.", severity: "success" });
            refresh();
            return;
          })
          .catch((error) => {
            setSaveInProgress(false);
            showAlert({
              message: apiReponseError(error, "Withdraw location registration Failed."),
              severity: "error",
            });
          });
      } catch (e) {
        setSaveInProgress(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Cancel order
  const showCancelOrderReason = () => {
    const lookupValues: Lookup[] = [];

    for (let i = 0; i < orderCancelReasons.length; i++) {
      lookupValues.push({
        value: orderCancelReasons[i].id,
        name: orderCancelReasons[i].name,
        checked: false,
      });
    }
    setOrderCancelReasonLookupValues(lookupValues.sort(compareArrayObjectValue("name", "asc")));
    setCancelOrderDialogOpen(true);
  };

  const cancelOrderReasonDialogConfirmation = async (confirmWithSelectReasonOutput: ConfirmWithSelectReasonOutput) => {
    setCancelOrderDialogOpen(false);
    if (confirmWithSelectReasonOutput.confirm) {
      const payload = {
        reasonId: confirmWithSelectReasonOutput.reasonId,
        note: confirmWithSelectReasonOutput.note,
      };

      try {
        setSaveInProgress(true);
        await API.post("/api/enlistmentadmin/locations/" + keys.locationItemKey + "/cancelorder", payload)
          .then(async (response) => {
            showAlert({ message: "Successfully saved.", severity: "success" });
            refresh();
            return;
          })
          .catch((error) => {
            setSaveInProgress(false);
            showAlert({
              message: apiReponseError(error, "Cancel order failed."),
              severity: "error",
            });
          });
      } catch (e) {
        setSaveInProgress(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Close group registration
  const showCloseGroupRegistrationReason = () => {
    const lookupValues: Lookup[] = [];
    for (let i = 0; i < orderCancelReasons.length; i++) {
      if (String(orderCancelReasons[i].name).indexOf("Wrong") === -1 && String(orderCancelReasons[i].name).indexOf("With") === -1) {
        lookupValues.push({
          value: orderCancelReasons[i].id,
          name: orderCancelReasons[i].name,
          checked: false,
        });
      }
    }
    setGroupRegistationCloseReasonLookupValues(lookupValues.sort(compareArrayObjectValue("name", "asc")));
    setGroupRegistationCloseDialogOpen(true);
  };

  const closeGroupRegistrationDialogConfirmation = async (confirmWithSelectReasonOutput: ConfirmWithSelectReasonOutput) => {
    setGroupRegistationCloseDialogOpen(false);
    if (confirmWithSelectReasonOutput.confirm) {
      const payload = {
        reasonId: confirmWithSelectReasonOutput.reasonId,
        note: confirmWithSelectReasonOutput.note,
      };

      try {
        setSaveInProgress(true);
        await API.post("/api/enlistmentadmin/locations/" + keys.locationItemKey + "/groupregistration/close", payload)
          .then(async (response) => {
            showAlert({ message: "Successfully saved.", severity: "success" });
            refresh();
            return;
          })
          .catch((error) => {
            setSaveInProgress(false);
            showAlert({
              message: apiReponseError(error, "Cancel order failed."),
              severity: "error",
            });
          });
      } catch (e) {
        setSaveInProgress(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Change program
  const showChangeProgram = () => {
    const lookupValues: Lookup[] = [];

    for (let i = 0; i < changePrograms.length; i++) {
      lookupValues.push({
        value: changePrograms[i].programCode,
        name: changePrograms[i].programName,
        checked: false,
      });
    }
    setChangeProgramLookupValues(lookupValues.sort(compareArrayObjectValue("name", "asc")));
    setChangeProgramDialogOpen(true);
  };

  const selectChangeProgram = async (confirmOutput: ConfirmOutput) => {
    setChangeProgramDialogOpen(false);

    if (confirmOutput.status === "select") {
      const payload = {
        changeProgramCode: confirmOutput.data.value,
      };

      try {
        setSaveInProgress(true);
        await API.post("/api/enlistmentadmin/locations/" + keys.locationItemKey + "/changeprogram", payload)
          .then(async (response) => {
            showAlert({ message: "Successfully saved.", severity: "success" });
            refresh();
            return;
          })
          .catch((error) => {
            setSaveInProgress(false);
            showAlert({
              message: apiReponseError(error, "Change program failed."),
              severity: "error",
            });
          });
      } catch (e) {
        setSaveInProgress(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Account address
  const checkAccountAddress = async () => {
    const payload = {
      address: {
        addressline1: formReference.current.values.account.address.addressline1,
        addressline2: formReference.current.values.account.address.addressline2,
        addressline3: "",
        country: formReference.current.values.account.address.country,
        locality1: formReference.current.values.account.address.locality1,
        locality2: "",
        postalCode: formReference.current.values.account.address.postalCode,
        region1: formReference.current.values.account.address.region1,
        region2: "",
      },
      allowPOBox: false,
      firmName: "",
    };

    const addressResultOutput = JSON.parse(JSON.stringify(accountAddressResultOutput));
    addressResultOutput.show = false;

    try {
      setAccountAddressValidationInProgress(true);
      setAccountAddressResultOutput(addressResultOutput);

      const apiResponse = await addressValidation(payload);
      if (apiResponse.success) {
        if (isValidJSONString(apiResponse.data.address)) {
          const address = apiResponse.data.address;

          if (address.valid) {
            addressResultOutput.valid = address.valid;
            addressResultOutput.message = address.friendlyMessage;
            addressResultOutput.address = address;
            addressResultOutput.addressLine = address.addressline1 + " " + address.addressline2;
            addressResultOutput.cityStatePostalCountry = address.locality1 + " " + address.region1 + " " + address.postalCode + " " + address.country;
            addressResultOutput.errorCodeTagged = address.errorCodeTagged;
            addressResultOutput.latitude = address.latitude;
            addressResultOutput.longitude = address.longitude;
            addressResultOutput.poBox = address.poBox;
            addressResultOutput.autoCorrect = address.autoCorrect;
            addressResultOutput.sameAddressLine = address.sameAddressLine;
            addressResultOutput.addressType = address.addressType;

            // if the address is valid, we need to set the slim eligibility result from 
            // the api
            formReference.current.setFieldValue("slimEligible", address.slimEligible);
            formReference.current.setFieldValue("latitude", address.latitude);
            formReference.current.setFieldValue("longitude", address.longitude);

          } else {
            addressResultOutput.valid = false;
            addressResultOutput.message = address.friendlyMessage;

            // if the address is not valid, then the location can't be slim eligible regardless
            // of what is returned from the API, set to false
            formReference.current.setFieldValue("slimEligible", false);
            formReference.current.setFieldValue("latitude", 0.0);
            formReference.current.setFieldValue("longitude", 0.0);
          }
        } else {
          addressResultOutput.valid = false;
        }
      } else {
        addressResultOutput.valid = false;
        addressResultOutput.message = apiResponse.errorMessage;
      }

      addressResultOutput.show = true;
      setAccountAddressResultOutput({ ...addressResultOutput });
      setAccountAddressValidationInProgress(false);
    } catch (e) {
      addressResultOutput.valid = false;
      addressResultOutput.message = "Something was wrong in our system.";
      addressResultOutput.show = true;
      setAccountAddressResultOutput({ ...addressResultOutput });
      setAccountAddressValidationInProgress(false);
    }
  };

  const useThisAccountAddress = () => {
    formReference.current.setFieldValue("account.address", accountAddressResultOutput.address);
    formReference.current.setFieldValue("overrideAccountAddressValidation", true);

    if (accountAddressResultOutput.address.country === "US") {
      formReference.current.setFieldValue("account.address.country", "USA");
    } else if (accountAddressResultOutput.address.country === "CA") {
      formReference.current.setFieldValue("account.address.country", "CAN");
    }

    setSaveButtonVisible(true);
    closeAddressMessageStatus("A");
    checkAccountAddress();
  };

  const setOverrideAccountAddressValidation = () => {
    formReference.current.setFieldValue("overrideAccountAddressValidation", false);

    if (showAddressValidationButton("A")) {
      formReference.current.setFieldValue("overrideAccountAddressValidation", true);
    }

    setSaveButtonVisible(true);
    closeAddressMessageStatus("A");
  };

  // Shipping address
  const checkShippingAddress = async () => {
    const payload = {
      address: {
        addressline1: formReference.current.values.shipping.address.addressline1,
        addressline2: formReference.current.values.shipping.address.addressline2,
        addressline3: "",
        country: formReference.current.values.shipping.address.country,
        locality1: formReference.current.values.shipping.address.locality1,
        locality2: "",
        postalCode: formReference.current.values.shipping.address.postalCode,
        region1: formReference.current.values.shipping.address.region1,
        region2: "",
      },
      allowPOBox: false,
      firmName: "",
    };

    const addressResultOutput = JSON.parse(JSON.stringify(shippingAddressResultOutput));
    addressResultOutput.show = false;

    try {
      setShippingAddressValidationInProgress(true);
      setShippingAddressResultOutput(addressResultOutput);

      const apiResponse = await addressValidation(payload);
      if (apiResponse.success) {
        if (isValidJSONString(apiResponse.data.address)) {
          const address = apiResponse.data.address;

          if (address.valid) {
            addressResultOutput.valid = address.valid;
            addressResultOutput.message = address.friendlyMessage;
            addressResultOutput.address = address;
            addressResultOutput.addressLine = address.addressline1 + " " + address.addressline2;
            addressResultOutput.cityStatePostalCountry = address.locality1 + " " + address.region1 + " " + address.postalCode + " " + address.country;
            addressResultOutput.errorCodeTagged = address.errorCodeTagged;
            addressResultOutput.latitude = address.latitude;
            addressResultOutput.longitude = address.longitude;
            addressResultOutput.poBox = address.poBox;
            addressResultOutput.autoCorrect = address.autoCorrect;
            addressResultOutput.sameAddressLine = address.sameAddressLine;
            addressResultOutput.addressType = address.addressType;
          } else {
            addressResultOutput.valid = false;
            addressResultOutput.message = address.friendlyMessage;
          }
        } else {
          addressResultOutput.valid = false;
        }
      } else {
        addressResultOutput.valid = false;
        addressResultOutput.message = apiResponse.errorMessage;
      }

      addressResultOutput.show = true;
      setShippingAddressResultOutput({ ...addressResultOutput });
      setShippingAddressValidationInProgress(false);
    } catch (e) {
      addressResultOutput.valid = false;
      addressResultOutput.message = "Something was wrong in our system.";
      addressResultOutput.show = true;
      setShippingAddressResultOutput({ ...addressResultOutput });
      setShippingAddressValidationInProgress(false);
    }
  };

  const useThisShippingAddress = () => {
    formReference.current.setFieldValue("shipping.address", shippingAddressResultOutput.address);
    formReference.current.setFieldValue("overrideShippingAddressValidation", true);

    if (shippingAddressResultOutput.address.country === "US") {
      formReference.current.setFieldValue("shipping.address.country", "USA");
    } else if (shippingAddressResultOutput.address.country === "CA") {
      formReference.current.setFieldValue("shipping.address.country", "CAN");
    }

    setSaveButtonVisible(true);
    closeAddressMessageStatus("S");
    checkShippingAddress();
  };

  const setOverrideShippingAddressValidation = () => {
    formReference.current.setFieldValue("overrideShippingAddressValidation", false);

    if (showAddressValidationButton("S")) {
      formReference.current.setFieldValue("overrideShippingAddressValidation", true);
    }

    setSaveButtonVisible(true);
    closeAddressMessageStatus("S");
  };

  // Address

  const closeAddressMessageStatus = (type: string) => {
    closeAddressMessageStatus2(type, false);
  };

  const closeAddressMessageStatus2 = (type: string, resetValidAttribute: boolean) => {
    if (type === "A") {
      const addressResultOutput = JSON.parse(JSON.stringify(accountAddressResultOutput));
      addressResultOutput.show = false;
      if (resetValidAttribute) {
        addressResultOutput.valid = false;

        if (isFormDataValid(formReference)) {
          formReference.current.setFieldValue("overrideAccountAddressValidation", false);
        }
      }
      setAccountAddressResultOutput({ ...addressResultOutput });
    } else if (type === "S") {
      const addressResultOutput = JSON.parse(JSON.stringify(shippingAddressResultOutput));
      addressResultOutput.show = false;
      if (resetValidAttribute) {
        addressResultOutput.valid = false;
        if (isFormDataValid(formReference)) {
          formReference.current.setFieldValue("overrideShippingAddressValidation", false);
        }
      }
      setShippingAddressResultOutput({ ...addressResultOutput });
    }
  };

  const resetAddressFields = (type: string) => {
    if (type === "A") {
      formReference.current.setFieldValue("account.address.addressline1", "");
      formReference.current.setFieldValue("account.address.addressline2", "");
      formReference.current.setFieldValue("account.address.locality1", "");
      formReference.current.setFieldValue("account.address.region1", "");
      formReference.current.setFieldValue("account.address.postalCode", "");
      formReference.current.setFieldTouched("account.address.addressline1", true);
      formReference.current.setFieldTouched("account.address.locality1", true);
      formReference.current.setFieldTouched("account.address.region1", true);
      formReference.current.setFieldTouched("account.address.postalCode", true);
      setTimeout(() => {
        formReference.current.validateForm();
      }, 100);
      setAccountAddressResultOutput(defaultAddressResultOutput);
    } else if (type === "S") {
      formReference.current.setFieldValue("shipping.address.addressline1", "");
      formReference.current.setFieldValue("shipping.address.addressline2", "");
      formReference.current.setFieldValue("shipping.address.locality1", "");
      formReference.current.setFieldValue("shipping.address.region1", "");
      formReference.current.setFieldValue("shipping.address.postalCode", "");
      formReference.current.setFieldTouched("shipping.address.addressline1", true);
      formReference.current.setFieldTouched("shipping.address.locality1", true);
      formReference.current.setFieldTouched("shipping.address.region1", true);
      formReference.current.setFieldTouched("shipping.address.postalCode", true);
      setTimeout(() => {
        formReference.current.validateForm();
      }, 100);
      setAccountAddressResultOutput(defaultAddressResultOutput);
    }
  };

  const showAddressValidationButton = (type: string) => {
    if (isFormReferenceValid(formReference)) {
      if (type === "A") {
        if (formReference.current.values.account.address.addressline1 !== "" && formReference.current.values.account.address.locality1 !== "" && formReference.current.values.account.address.region1 !== "" && formReference.current.values.account.address.postalCode !== "" && formReference.current.values.account.address.country !== "") {
          return true;
        }
      } else if (type === "S") {
        if (formReference.current.values.shipping.address.addressline1 !== "" && formReference.current.values.shipping.address.locality1 !== "" && formReference.current.values.shipping.address.region1 !== "" && formReference.current.values.shipping.address.postalCode !== "" && formReference.current.values.shipping.address.country !== "") {
          return true;
        }
      }
    }
    return false;
  };

  const handleAddressChanged = (type: string) => {
    closeAddressMessageStatus2(type, true);
  };

  const handleCountryChanged = (event: any, type: string) => {
    if (isFormReferenceValid(formReference)) {
      if (type === "A") {
        formReference.current.setFieldValue("account.address.region1", "");
      } else if (type === "S") {
        formReference.current.setFieldValue("shipping.address.region1", "");
      }
    }
    getStates(event.target.value, type);
  };

  const getStates = (country: string, type: string) => {
    if (type === "A") {
      setAccountStates(filterCountryStates(referenceLookups.countries, country));
    } else if (type === "S") {
      setShippingStates(filterCountryStates(referenceLookups.countries, country));
    }
  };

  // Location
  const getKitDevices = (kitNo: number) => {
    setKitDevices([]);
     const filterKits = referenceLookups.kits.filter((kit) => kit.hsbKitNo.toString() === kitNo.toString());
    if (filterKits.length > 0) {
      setKitDevices(filterKits[0].devices);
    }
  };

const loadDefaultInstallOption = (kitNo: number) => {
  
  if (kitNo > 0) {    
    const InstallOption = locationKits.filter((kit) => kit.kitNo.toString() === kitNo.toString());
    if (InstallOption.length > 0) {
      const installMethod = InstallOption[0].installMethod;
      if (installMethod > 0) {
        formReference.current.values.installMethodId=installMethod;
        formReference.current.setFieldValue("installMethodId", installMethod);
        formReference.current.setFieldError("installMethodId", ""); // Clear the error
        formReference.current.setFieldTouched("installMethodId", false); // Clear the touched state
      } else {
        formReference.current.setFieldError("installMethodId", "Install method not matching with our values.");
      }
    } else {
      formReference.current.setFieldError("installMethodId", "No matching kit found.");
    }
  }
  else {
  formReference.current.values.installMethodId=0;
    formReference.current.setFieldValue("installMethodId", 0);           
        formReference.current.setFieldTouched("installMethodId", true);
        formReference.current.setFieldError("installMethodId", "Install method not matching with our values.");
        formReference.current.setFieldError("kitNo", "Kit is required.");
        scrollToFieldError(formReference);
  } 
 
};

  const downloadLocationTaxExemptFile = async () => {
    const apiResponse = await downloadEnlistmentProgramLocationTaxExemptFile(keys.locationItemKey);

    if (apiResponse.success) {
      const binaryString = window.atob(apiResponse.data.content);
      const bytes = new Uint8Array(binaryString.length);
      const content = bytes.map((byte, i) => binaryString.charCodeAt(i));

      const blob = new Blob([content]);
      const fileName = apiResponse.data.fileName;
      if (navigator.msSaveBlob) {
        // IE 10+
        navigator.msSaveBlob(blob, fileName);
      } else {
        const link = document.createElement("a");
        // Browsers that support HTML5 download attribute
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", fileName);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      cancel();
    }
  };

  const getLocationData = async () => {
    if (!canReadEnlistment(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("home");
      return;
    }

    setLoading(true);
    setReadonly(true);
    setSaveButtonVisible(false);
    setSaveInProgress(false);
    setOnHold(false);
    setIsGroup(false);
    setIsParent(false)
    setLocationKits([]);
    setKitDevices([]);

    closeAddressMessageStatus2("A", false);
    closeAddressMessageStatus2("S", false);

    const apiResponse = await getEnlistmentProgramLocation(keys.partnerKey, keys.programKey, keys.locationItemKey);
    if (apiResponse.success) {
      const currentOverrideAccountAddressValidation = apiResponse.data.location.overrideAccountAddressValidation;
      const currentOverrideShippingAddressValidation = apiResponse.data.location.overrideShippingAddressValidation;
      const status = apiResponse.data.location.reviewLocationOtherAttributes.status;
      const noofAccounts = apiResponse.data.location.reviewLocationOtherAttributes.noofAccounts;

      if (apiResponse.data.location.reviewLocationOtherAttributes.isParent) {
        setIsParent(true);
      }

      if (apiResponse.data.location.account.address.country === "US") {
        apiResponse.data.location.account.address.country = "USA";
      }

      if (apiResponse.data.location.shipping.address.country === "US") {
        apiResponse.data.location.shipping.address.country = "USA";
      }

      if (noofAccounts >= 11 && apiResponse.data.location.account.accountNumber === "") {
        apiResponse.data.location.account.accountNumber = ".";
      }

      setInitialFormValues(apiResponse.data.location);
      setLocationKits(apiResponse.data.locationKits);
      setChangePrograms(apiResponse.data.programs);
      setOrderCancelReason(apiResponse.data.orderCancelReasons);

      getStates(apiResponse.data.location.account.address.country, "A");
      getStates(apiResponse.data.location.shipping.address.country, "S");
      getKitDevices(apiResponse.data.location.kitNo as number);

      const validStatus = [1, 3, 4];

      if (canReadEnlistment(userFunctionAccess)) {
        setReadonly(true);
      }

      if (canWriteEnlistment(userFunctionAccess)) {
        if (validStatus.indexOf(status) >= 0) {
          if (status === 1) {
            setSaveButtonVisible(true);
          }
        }

        let checkAddress = false;

        if ([95, 96, 97, 98, 99, 100].indexOf(status) >= 0) {
          setReadonly(true);
        } else if (status === 2) {
          setReadonly(true);
        } else if (status === 4) {
          setReadonly(true);
        } else if (status === 94) {
          setOnHold(true);
          setReadonly(true);
        } else if (noofAccounts >= 11) {
          setIsGroup(true);
          setReadonly(true);
        } else {
          setReadonly(false);
          checkAddress = true;
        }

        setTimeout(() => {
          if (checkAddress && !currentOverrideAccountAddressValidation) {
            checkAccountAddress();
          }

          if (checkAddress && !currentOverrideShippingAddressValidation) {
            checkShippingAddress();
          }
        }, 1000);

        setLoading(false);

        setTimeout(() => {
          displayError();
          scrollToFieldError(formReference);
        }, 5000);
      } else {
        setLoading(false);
      }
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      cancel();
    }
  };

  const displayError = () => {
    if (readonly || isGroup) {
      return;
    }

    const errorMessage = formReference.current.values.reviewLocationOtherAttributes.errorMessage;

    setValidationErrorMessage("");
    if (errorMessage !== "" && errorMessage !== null && errorMessage !== undefined) {
      const errors = errorMessage.split(".");

      for (let i = 0; i < errors.length; i++) {
        if (errors[i].toLowerCase().indexOf("account number") >= 0) {
          formReference.current.setFieldError("account.accountNumber", errors[i]);
          errors.splice(i, 1);
          i = -1;
        } else if (errors[i].toLowerCase().indexOf("policy number") >= 0) {
          formReference.current.setFieldError("account.accountNumber", errors[i]);
          errors.splice(i, 1);
          i = -1;
        } else if (errors[i].toLowerCase().indexOf("parent account number") >= 0) {
          formReference.current.setFieldError("parentAccountNumber", errors[i]);
          errors.splice(i, 1);
          i = -1;
        } else if (errors[i].toLowerCase().indexOf("package") >= 0) {
          formReference.current.setFieldError("kitNo", errors[i]);
          errors.splice(i, 1);
          i = -1;
        } else if (errors[i].toLowerCase().indexOf("signup key") >= 0) {
          formReference.current.setFieldError("signupKey", errors[i]);
          errors.splice(i, 1);
          i = -1;
        }
      }
      setValidationErrorMessage(errors.join(","));
      return errors.join(",");
    }
  };

  // Save Form
  const submitForm = () => {
    if (!scrollToFieldError(formReference)) {
      formReference.current.handleSubmit();
    }
  };

  const scrollToFieldError = (formRef: any) => {
    if (readonly || isGroup) {
      return;
    }

    let returnValue = false;
    if (!formRef.current.isValid) {
      try {
        Object.keys(formRef.current.errors).forEach((key) => {
          let element = document.querySelector(`input[name='${key}']`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            returnValue = true;
            return;
          } else {
            element = document.querySelector(`select[name='${key}']`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
              returnValue = true;
              return;
            }
          }
        });
      } catch (e) { }

      showAlert({
        message: "One or more validation errors occurred.",
        severity: "error",
      });
    }
    return returnValue;
  };

  const saveOnHold = async () => {
    const payload = {
      status: !initialFormValues.reviewLocationOtherAttributes.onHold,
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/enlistmentadmin/locations/" + keys.locationItemKey + "/onhold", payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          setRefreshList(true);
          await getLocationData();
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to save location item on hold."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const saveEnroll = async () => {
    const payload = {};

    try {
      setSaveInProgress(true);
      await API.post("/api/enlistmentadmin/locations/" + keys.locationItemKey + "/enroll", payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          refresh();
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to save location item to enroll."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const save = async () => {
    let externalDataChecked = true;

    if (isParent) {
      formReference.current.setFieldValue("installMethodId", formReference.current.values.installMethodId);
      formReference.current.setFieldValue("kitNo", -1);
      formReference.current.setFieldValue("shipping.organizationName", formReference.current.values.account.accountName);
      formReference.current.setFieldValue("shipping.firstName", formReference.current.values.contact.firstName);
      formReference.current.setFieldValue("shipping.lastName", formReference.current.values.contact.lastName);
      formReference.current.setFieldValue("shipping.phone", formReference.current.values.contact.phone);
      formReference.current.setFieldValue("shipping.email", formReference.current.values.contact.email);
      formReference.current.setFieldValue("shipping.address", formReference.current.values.account.address);
      formReference.current.setFieldValue("overrideShippingAddressValidation", formReference.current.values.overrideAccountAddressValidation);
    }

    if (!isParent && formReference.current.values.kitNo <= 0) {
      formReference.current.setFieldError("kitNo", "Kit is required.");
      scrollToFieldError(formReference);
      return;
    }
    else if (isParent && formReference.current.values.kitNo !== -1) {
      formReference.current.setFieldError("kitNo", "Kit is required.");
      scrollToFieldError(formReference);
      return;
    }

    if (!accountAddressResultOutput.valid && !formReference.current.values.overrideAccountAddressValidation) {
      externalDataChecked = false;
      checkAccountAddress();
    }

    if (!shippingAddressResultOutput.valid && !formReference.current.values.overrideShippingAddressValidation) {
      externalDataChecked = false;
      checkShippingAddress();
    }

    if (!externalDataChecked) {
      showAlert({
        message: "Please wait checking address...",
        severity: "info",
      });

      setTimeout(() => {
        showAlert({
          message: "Please press 'Update or Review & Update' button one more time to continue.",
          severity: "info",
        });
      }, 800);

      return false;
    }

    if (accountAddressResultOutput.valid) {
      formReference.current.setFieldValue("overrideAccountAddressValidation", true);
    }

    if (shippingAddressResultOutput.valid) {
      formReference.current.setFieldValue("overrideShippingAddressValidation", true);
    }


    formReference.current.validateForm();

    if (!formReference.current.isValid) {
      return;
    }

    const payload = {
      location: {
        account: {
          accountName: formReference.current.values.account.accountName,
          accountNumber: formReference.current.values.account.accountNumber,
          address: {
            addressline1: formReference.current.values.account.address.addressline1,
            addressline2: formReference.current.values.account.address.addressline2,
            addressline3: formReference.current.values.account.address.addressline3,
            country: formReference.current.values.account.address.country,
            locality1: formReference.current.values.account.address.locality1,
            locality2: formReference.current.values.account.address.locality2,
            postalCode: formReference.current.values.account.address.postalCode,
            region1: formReference.current.values.account.address.region1,
            region2: formReference.current.values.account.address.region2,
          },
          group: formReference.current.values.account.group,
          premise: formReference.current.values.account.premise,
        },
        adminMetadataValues: formReference.current.values.adminMetadataValues,
        contact: {
          email: formReference.current.values.contact.email,
          firstName: formReference.current.values.contact.firstName,
          lastName: formReference.current.values.contact.lastName,
          phone: formReference.current.values.contact.phone,
        },
        parentAccountNumber: formReference.current.values.parentAccountNumber,
        installMethodId: formReference.current.values.installMethodId,
        kitNo: formReference.current.values.kitNo,
        overrideAccountAddressValidation: formReference.current.values.overrideAccountAddressValidation,
        overrideShippingAddressValidation: formReference.current.values.overrideShippingAddressValidation,
        programCode: formReference.current.values.programCode,
        shipping: {
          address: {
            addressline1: formReference.current.values.shipping.address.addressline1,
            addressline2: formReference.current.values.shipping.address.addressline2,
            addressline3: formReference.current.values.shipping.address.addressline3,
            country: formReference.current.values.shipping.address.country,
            locality1: formReference.current.values.shipping.address.locality1,
            locality2: formReference.current.values.shipping.address.locality2,
            postalCode: formReference.current.values.shipping.address.postalCode,
            region1: formReference.current.values.shipping.address.region1,
            region2: formReference.current.values.shipping.address.region2,
          },
          email: formReference.current.values.shipping.email,
          firstName: formReference.current.values.shipping.firstName,
          lastName: formReference.current.values.shipping.lastName,
          organizationName: formReference.current.values.shipping.organizationName,
          phone: formReference.current.values.shipping.phone,
        },
        signupKey: formReference.current.values.signupKey,
        slimEligible: formReference.current.values.slimEligible,
        latitude: formReference.current.values.latitude,
        longitude: formReference.current.values.longitude,
      },
    };



    try {
   
      setSaveInProgress(true);
      await API.post("/api/enlistmentadmin/locations/" + keys.locationItemKey, payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          await getLocationData();
          setRefreshList(true);
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "location.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save location review data."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const refresh = () => {
    const confirmOutput: ConfirmOutput = { status: "refresh", data: "" };
    onCallback(confirmOutput);
  };

  const cancel = () => {
    if (refreshList) {
      refresh();
      return;
    }
    const confirmOutput: ConfirmOutput = { status: "cancel", data: "" };
    onCallback(confirmOutput);
  };

  const cleanPhoneNumber = (phone, type) => {
    let phoneNumber = phone;

    try {
      phoneNumber = String(phone).trim();
      phoneNumber = phoneNumber.replaceAll("(1", "").replaceAll("+1", "");
      phoneNumber = phoneNumber.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(".", "").replaceAll("+", "").replaceAll(" ", "");
    } catch { }

    if (type === "contact") {
      formReference.current.setFieldValue("contact.phone", phoneNumber);
      formReference.current.setFieldTouched("contact.phone", true, true);
    }
    else if (type === "shipping") {
      formReference.current.setFieldValue("shipping.phone", phoneNumber);
      formReference.current.setFieldTouched("shipping.phone", true, true);
    }

  };

  useEffect(() => {
    if (open) {
      if (keys.locationItemKey !== "" && keys.locationItemKey !== undefined && keys.locationItemKey !== null) {
        setLoading(true);
        getLocationData();
      }
      else {
        cancel();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, keys]);

  return (
    <>
      <ConfirmWithReason title="Withdraw Confirmation" content={""} yesText={"CONFIRM"} noText="CANCEL" open={withdrawDialogOpen} showReason={true} required={true} reasonLabel={"Reason"} callbackOnConfirm={withdrawReasonDialogConfirmation} />
      <ConfirmWithSelectReason title="Order cancellation Confirmation" content={""} yesText={"CONFIRM"} noText="CANCEL" open={cancelOrderDialogOpen} reasonIdLabel={"Reason"} reasonNoteLabel={"Note"} reasonLookups={orderCancelReasonLookupValues} callbackOnConfirm={cancelOrderReasonDialogConfirmation} />
      <ConfirmWithSelectReason title="Close Confirmation" content={""} yesText={"CONFIRM"} noText="CANCEL" open={groupRegistationCloseDialogOpen} reasonIdLabel={"Reason"} reasonNoteLabel={"Note"} reasonLookups={groupRegistationCloseReasonLookupValues} callbackOnConfirm={closeGroupRegistrationDialogConfirmation} />
      <ConfirmSelect title="Select Program" label={"Program"} content={``} data={changeProgramLookupValues} yesText="SELECT" noText="CANCEL" open={changeProgramDialogOpen} multiSelect={false} maxSelect={0} callbackOnSelect={selectChangeProgram} />

      <ParentAccountSearch locationItemKey={keys.locationItemKey} open={openParentAccountSearchDialog} callActionButton={confirmParentAccountSearchDialog} />

      <CreateParentAccount locationItemKey={keys.locationItemKey} open={openCreateParentAccountDialog} callActionButton={confirmCreateParentAccountDialog} />

      <Dialog className={globalStyles.dialogContainer} open={open} maxWidth={"md"} fullWidth={true}>
        <DialogTitle className={globalStyles.dialogTitleContainer}>
          <Typography variant="body1">{readonly ? "Enlistment View" : "Enlistment Review"}</Typography>
        </DialogTitle>
        {loading ? (
          <>
            <DialogContent dividers>
              <Wait />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => cancel()} color="secondary" autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent dividers>
              <SaveBackdrop open={saveInProgress} />

              <Formik
                innerRef={formReference}
                initialValues={initialFormValues}
                validationSchema={formValidationSchema}
                validateOnMount={true}
                initialTouched={formDefaultTouchValue}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values: EnlistmentReviewAccount) => {
                  save();
                }}
              >
                {({ values, errors, handleChange, setFieldValue, touched, handleBlur, isSubmitting, validateForm }) => (
                  <Form
                    onChange={() => {
                      setSaveButtonVisible(true);
                    }}
                  >
                    <Grid container spacing={3} className={globalStyles.formikContainer}>
                      {!readonly && values.reviewLocationOtherAttributes.isError ? (
                        <Grid item xs={12}>
                          <Box p={1}></Box>
                          <Alert severity="error">
                            <AlertTitle>Immediate Action Required</AlertTitle>
                            {validationErrorMessage !== "" ? validationErrorMessage : "Validation failed. Required information is missing or not valid."}
                          </Alert>
                          <Box p={1}></Box>
                        </Grid>
                      ) : null}

                      {isGroup ? (
                        <Grid item xs={12}>
                          <Box p={1}></Box>
                          <Alert severity="info">
                            <AlertTitle>Registration Inquiry</AlertTitle>A customer interested in registering 11 or more locations and contact information is below.
                          </Alert>
                          <Box p={1}></Box>
                        </Grid>
                      ) : null}

                      {values.reviewLocationOtherAttributes.status === 2 && values.reviewLocationOtherAttributes.subscriptionBilling ? (
                        <Grid item xs={12}>
                          <Box p={1}></Box>
                          <Alert severity="error">
                            <AlertTitle>Awaiting For Confirmation</AlertTitle>
                            Subscription Billing Payment Pending
                          </Alert>
                          <Box p={1}></Box>
                        </Grid>
                      ) : null}

                      {onHold ? (
                        <Grid item xs={12}>
                          <Box p={3}>
                            <Alert severity="info">
                              <AlertTitle>Action Required</AlertTitle>
                              The registration is currently on hold. Click the <b>Activate</b> button to continue the process.
                            </Alert>
                          </Box>
                        </Grid>
                      ) : null}

                      <Grid item xs={12} >
                        <TextField disabled={true} value={values.reviewLocationOtherAttributes.partnerName} label={"Company"} fullWidth />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth={true}>
                          <InputLabel>Program</InputLabel>
                          <Input
                            required
                            disabled={true}
                            value={values.programCode > 0 ? `${values.reviewLocationOtherAttributes.programName} - ${values.programCode}` : values.programCode}
                            error={Boolean(getIn(errors, "programCode"))}
                            endAdornment={
                              <InputAdornment position="end">
                                {!readonly && !isParent && !initialFormValues.reviewLocationOtherAttributes.subscriptionBilling && changePrograms?.length > 0 ? (
                                  <React.Fragment>
                                    <Tooltip arrow title="Change Program">
                                      <IconButton onClick={showChangeProgram}>
                                        <ChangeProgramIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </React.Fragment>
                                ) : (
                                  <React.Fragment></React.Fragment>
                                )}
                              </InputAdornment>
                            }
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} >
                        <FormControl fullWidth={true}>
                          <InputLabel>Parent Account</InputLabel>
                          <Input
                            required
                            disabled={true}
                            value={values.parentAccountNumber !== "" ? `${values.reviewLocationOtherAttributes.parentAccountName} - ${values.parentAccountNumber}` : ""}
                            error={Boolean(getIn(errors, "parentAccountNumber"))}
                            endAdornment={
                              <InputAdornment position="end">
                                {!readonly && !isParent ? (
                                  <React.Fragment>
                                    <Tooltip arrow title="Add New Parent Account">
                                      <IconButton onClick={showCreateParentAccountDialog}>
                                        <AddIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip arrow title="Search Accounts">
                                      <IconButton onClick={showParentAccountSearchDialog}>
                                        <SearchIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </React.Fragment>
                                ) : (
                                  <React.Fragment></React.Fragment>
                                )}
                              </InputAdornment>
                            }
                          />
                        </FormControl>

                      </Grid>

                      {/* {!isGroup ? (
                        <Grid item xs={12} sm={12} md={6} lg={4}>
                          <TextField
                            disabled={readonly}
                            id="signupKey"
                            name="signupKey"
                            label="Signup Key"
                            value={values.signupKey}
                            onChange={(e) => {
                              handleChange(e);
                              handleAddressChanged("A");
                            }}
                            onBlur={handleBlur}
                            error={Boolean(getIn(errors, "signupKey"))}
                            helperText={getIn(errors, "signupKey")}
                          />
                        </Grid>
                      ) : null} */}
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <TextField disabled={true} value={values.reviewLocationOtherAttributes.statusName} label={"Status"} fullWidth />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <TextField disabled={true} value={values.reviewLocationOtherAttributes.accountGroupName} label={"Group Name"} fullWidth />
                      </Grid>
                      <Grid item xs={12}>
                        <Box p={1}></Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box className={globalStyles.topic}>
                          {accountAddressValidationInProgress ? <LinearProgress color="secondary" /> : null}
                          <Grid container>
                            <Grid item xs={8}>
                              Account
                            </Grid>
                            <Grid item xs={4}>
                              <Box display="flex" justifyContent="flex-end" width={1}>
                                {!readonly ? (
                                  <React.Fragment>
                                    {!accountAddressValidationInProgress ? (
                                      <>
                                        {showAddressValidationButton("A") ? (
                                          <Tooltip arrow title="Validate address">
                                            <IconButton onClick={() => checkAccountAddress()}>
                                              <ValidateAddressIcon color="secondary" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : null}
                                      </>
                                    ) : null}
                                    <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                    <Tooltip arrow title="Clear address">
                                      <IconButton onClick={() => resetAddressFields("A")}>
                                        <ClearAllIcon color="error" />
                                      </IconButton>
                                    </Tooltip>
                                  </React.Fragment>
                                ) : null}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      {accountAddressResultOutput.show ? (
                        <Grid item xs={12}>
                          <DisplayAddressResult
                            addressResultOutput={accountAddressResultOutput}
                            currentAddress={values.account.address}
                            callbackOnClose={() => {
                              closeAddressMessageStatus("A");
                            }}
                            useThisAddressButton={true}
                            callbackOnUseThisAddress={useThisAccountAddress}
                            overrideValidationAddress={true}
                            callbackOnOverrideValidationAddress={setOverrideAccountAddressValidation}
                          />
                        </Grid>
                      ) : null}

                      {/* check if the location is eligible for slim */}

                      {!values.slimEligible ? (
                      <Grid item xs={12}>
                        <Alert severity="error">Location does not have sidewalk coverage</Alert>
                      </Grid>
                      ) : null}

                      <Grid item xs={12}>
                        {/* Account */}

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6} md={3} lg={4}>
                            <TextField disabled={readonly} id="account.accountNumber" name="account.accountNumber" value={values.account.accountNumber} label={values.reviewLocationOtherAttributes.accountNumberFormTitle === "" ? "Account Number" : values.reviewLocationOtherAttributes.accountNumberFormTitle} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "account.accountNumber"))} helperText={getIn(errors, "account.accountNumber")} />
                          </Grid>
                          <Grid item xs={12} sm={12} md={9} lg={8}>
                            <TextField disabled={readonly} id="account.accountName" name="account.accountName" value={values.account.accountName} label={values.reviewLocationOtherAttributes.accountNameFormTitle === "" ? "Account Name" : values.reviewLocationOtherAttributes.accountNameFormTitle} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "account.accountName"))} helperText={getIn(errors, "account.accountName")} />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Account Address */}
                      {values.overrideAccountAddressValidation ? (
                        <Grid item xs={12}>
                          <Alert severity="info">The participant or admin has reviewed and verified the accuracy of this account address.</Alert>
                        </Grid>
                      ) : (
                        <></>
                      )}

                      <Grid item xs={12} sm={9} md={6} lg={4}>
                        <TextField
                          disabled={readonly}
                          id="addressline1"
                          name="account.address.addressline1"
                          label="Address Line 1"
                          value={values.account.address.addressline1}
                          onChange={(e) => {
                            handleChange(e);
                            handleAddressChanged("A");
                          }}
                          onBlur={handleBlur}
                          error={getIn(errors, "account.address.addressline1")}
                          helperText={getIn(errors, "account.address.addressline1")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={9} md={6} lg={4}>
                        <TextField
                          disabled={readonly}
                          id="account.address.addressline2"
                          name="account.address.addressline2"
                          label="Address Line 2"
                          value={values.account.address.addressline2}
                          onChange={(e) => {
                            handleChange(e);
                            handleAddressChanged("A");
                          }}
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid item xs={12}></Grid>
                      <Grid item xs={12} sm={6} md={3} lg={4}>
                        <TextField
                          disabled={readonly}
                          id="account.address.locality1"
                          name="account.address.locality1"
                          label="City"
                          value={values.account.address.locality1}
                          onChange={(e) => {
                            handleChange(e);
                            handleAddressChanged("A");
                          }}
                          onBlur={handleBlur}
                          error={Boolean(getIn(errors, "account.address.locality1"))}
                          helperText={getIn(errors, "account.address.locality1")}
                        />
                      </Grid>
                      <Grid item xs={6} sm={6} md={3} lg={4}>
                        <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "account.address.region1"))}>
                          <InputLabel id="state-label" shrink={true}>
                            State
                          </InputLabel>
                          <Select
                            disabled={readonly}
                            native
                            labelId="state-label"
                            id="account.address.region1"
                            name="account.address.region1"
                            value={values.account.address.region1}
                            onChange={(e) => {
                              handleChange(e);
                              handleAddressChanged("A");
                            }}
                            label="State"
                            error={Boolean(getIn(errors, "account.address.region1"))}
                          >
                            <option value={""}></option>
                            {accountStates.map((state, index) => (
                              <option value={state.code} key={index}>
                                {state.name}
                              </option>
                            ))}
                          </Select>

                          <FormHelperText error>{getIn(errors, "account.address.region1")}</FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3} lg={4}>
                        <TextField
                          disabled={readonly}
                          id="account.address.postalCode"
                          name="account.address.postalCode"
                          label="Postal Code"
                          value={values.account.address.postalCode}
                          onChange={(e) => {
                            handleChange(e);
                            handleAddressChanged("A");
                          }}
                          onBlur={handleBlur}
                          error={Boolean(getIn(errors, "account.address.postalCode"))}
                          helperText={getIn(errors, "account.address.postalCode")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} lg={4}>
                        <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "account.address.country"))}>
                          <InputLabel id="country-label" shrink={true}>
                            Country
                          </InputLabel>
                          <Select
                            disabled={readonly}
                            native
                            labelId="country-label"
                            id="account.address.country"
                            name="account.address.country"
                            value={values.account.address.country}
                            onChange={(e) => {
                              handleAddressChanged("A");
                              handleChange(e);
                              handleCountryChanged(e, "A");
                            }}
                            label="Country"
                            error={Boolean(getIn(errors, "account.address.country"))}
                          >
                            {referenceLookups.countries.map((country, index) => (
                              <option value={country.code} key={index}>
                                {country.name}
                              </option>
                            ))}
                          </Select>
                          <FormHelperText error>{getIn(errors, "account.address.country")}</FormHelperText>
                        </FormControl>
                      </Grid>

                      {/* Contact */}
                      <Grid item xs={12}>
                        <Box p={1}></Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box className={globalStyles.topic}>
                          <Grid container>
                            <Grid item xs={8}>
                              Contact
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6} md={6} lg={4}>
                        <TextField disabled={readonly} id="contact.firstName" name="contact.firstName" value={values.contact.firstName} label={"First Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "contact.firstName"))} helperText={getIn(errors, "contact.firstName")} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={4}>
                        <TextField disabled={readonly} id="contact.lastName" name="contact.lastName" value={values.contact.lastName} label={"Last Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "contact.lastName"))} helperText={getIn(errors, "contact.lastName")} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={4}>
                        <TextField disabled={readonly} id="contact.phone" name="contact.phone" value={values.contact.phone} label={"Phone (Preferred Mobile#)"} fullWidth
                          onChange={(e) => {
                            handleChange(e);
                            setTimeout(() => {
                              cleanPhoneNumber(e.target.value, "contact");
                            }, 1000);
                          }}
                          onBlur={handleBlur} error={Boolean(getIn(errors, "contact.phone"))} helperText={getIn(errors, "contact.phone")} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={8}>
                        <TextField disabled={readonly} id="contact.email" name="contact.email" value={values.contact.email} label={"Email"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "contact.email"))} helperText={getIn(errors, "contact.email")} />
                      </Grid>

                      {/* Shipping */}
                      {!isGroup ? (
                        <>
                          <Grid item xs={12}>
                            <Box p={1}></Box>
                          </Grid>

                          {
                            !isParent ?
                              <>
                                <Grid item xs={12}>
                                  <Box className={globalStyles.topic}>
                                    {shippingAddressValidationInProgress ? <LinearProgress color="secondary" /> : null}
                                    <Grid container>
                                      <Grid item xs={8}>
                                        Shipping
                                      </Grid>
                                      <Grid item xs={4}>
                                        <Box display="flex" justifyContent="flex-end" width={1}>
                                          {!readonly ? (
                                            <React.Fragment>
                                              {!shippingAddressValidationInProgress ? (
                                                <>
                                                  {showAddressValidationButton("S") ? (
                                                    <Tooltip arrow title="Validate address">
                                                      <IconButton onClick={() => checkShippingAddress()}>
                                                        <ValidateAddressIcon color="secondary" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  ) : null}
                                                </>
                                              ) : null}
                                              <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                              <Tooltip arrow title="Clear address">
                                                <IconButton onClick={() => resetAddressFields("S")}>
                                                  <ClearAllIcon color="error" />
                                                </IconButton>
                                              </Tooltip>
                                            </React.Fragment>
                                          ) : null}
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Grid>

                                {shippingAddressResultOutput.show ? (
                                  <Grid item xs={12}>
                                    <DisplayAddressResult
                                      addressResultOutput={shippingAddressResultOutput}
                                      currentAddress={values.shipping.address}
                                      callbackOnClose={() => {
                                        closeAddressMessageStatus("S");
                                      }}
                                      useThisAddressButton={true}
                                      callbackOnUseThisAddress={useThisShippingAddress}
                                      overrideValidationAddress={true}
                                      callbackOnOverrideValidationAddress={setOverrideShippingAddressValidation}
                                    />
                                  </Grid>
                                ) : (
                                  <></>
                                )}

                                {/* Shipping */}

                                <Grid item xs={12}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} sm={12} md={6} lg={8}>
                                      <TextField disabled={readonly} id="shipping.organizationName" name="shipping.organizationName" value={values.shipping.organizationName} label={"Organization Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "shipping.organizationName"))} helperText={getIn(errors, "shipping.organizationName")} />
                                    </Grid>
                                  </Grid>
                                </Grid>

                                {/* Shipping Address */}

                                {values.overrideShippingAddressValidation ? (
                                  <Grid item xs={12}>
                                    <Alert severity="info">The participant or admin has reviewed and verified the accuracy of this shipping address.</Alert>
                                  </Grid>
                                ) : null}

                                <Grid item xs={12} sm={9} md={6} lg={4}>
                                  <TextField
                                    disabled={readonly}
                                    id="addressline1"
                                    name="shipping.address.addressline1"
                                    label="Address Line 1"
                                    value={values.shipping.address.addressline1}
                                    onChange={(e) => {
                                      handleChange(e);
                                      handleAddressChanged("S");
                                    }}
                                    onBlur={handleBlur}
                                    error={getIn(errors, "shipping.address.addressline1")}
                                    helperText={getIn(errors, "shipping.address.addressline1")}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={9} md={6} lg={4}>
                                  <TextField
                                    disabled={readonly}
                                    id="shipping.address.addressline2"
                                    name="shipping.address.addressline2"
                                    label="Address Line 2"
                                    value={values.shipping.address.addressline2}
                                    onChange={(e) => {
                                      handleChange(e);
                                      handleAddressChanged("S");
                                    }}
                                    onBlur={handleBlur}
                                  />
                                </Grid>
                                <Grid item xs={12}></Grid>
                                <Grid item xs={12} sm={6} md={3} lg={4}>
                                  <TextField
                                    disabled={readonly}
                                    id="shipping.address.locality1"
                                    name="shipping.address.locality1"
                                    label="City"
                                    value={values.shipping.address.locality1}
                                    onChange={(e) => {
                                      handleChange(e);
                                      handleAddressChanged("S");
                                    }}
                                    onBlur={handleBlur}
                                    error={Boolean(getIn(errors, "shipping.address.locality1"))}
                                    helperText={getIn(errors, "shipping.address.locality1")}
                                  />
                                </Grid>
                                <Grid item xs={6} sm={6} md={3} lg={4}>
                                  <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "shipping.address.region1"))}>
                                    <InputLabel id="state-label" shrink={true}>
                                      State
                                    </InputLabel>
                                    <Select
                                      disabled={readonly}
                                      native
                                      labelId="state-label"
                                      id="shipping.address.region1"
                                      name="shipping.address.region1"
                                      value={values.shipping.address.region1}
                                      onChange={(e) => {
                                        handleChange(e);
                                        handleAddressChanged("S");
                                      }}
                                      label="State"
                                      error={Boolean(getIn(errors, "shipping.address.region1"))}
                                    >
                                      <option value={""}></option>
                                      {shippingStates.map((state, index) => (
                                        <option value={state.code} key={index}>
                                          {state.name}
                                        </option>
                                      ))}
                                    </Select>

                                    <FormHelperText error>{getIn(errors, "shipping.address.region1")}</FormHelperText>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={6} md={3} lg={4}>
                                  <TextField
                                    disabled={readonly}
                                    id="shipping.address.postalCode"
                                    name="shipping.address.postalCode"
                                    label="Postal Code"
                                    value={values.shipping.address.postalCode}
                                    onChange={(e) => {
                                      handleChange(e);
                                      handleAddressChanged("S");
                                    }}
                                    onBlur={handleBlur}
                                    error={Boolean(getIn(errors, "shipping.address.postalCode"))}
                                    helperText={getIn(errors, "shipping.address.postalCode")}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3} lg={4}>
                                  <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "shipping.address.country"))}>
                                    <InputLabel id="country-label" shrink={true}>
                                      Country
                                    </InputLabel>
                                    <Select
                                      disabled={readonly}
                                      native
                                      labelId="country-label"
                                      id="shipping.address.country"
                                      name="shipping.address.country"
                                      value={values.shipping.address.country}
                                      onChange={(e) => {
                                        handleAddressChanged("S");
                                        handleChange(e);
                                        handleCountryChanged(e, "S");
                                      }}
                                      label="Country"
                                      error={Boolean(getIn(errors, "shipping.address.country"))}
                                    >
                                      {referenceLookups.countries.map((country, index) => (
                                        <option value={country.code} key={index}>
                                          {country.name}
                                        </option>
                                      ))}
                                    </Select>
                                    <FormHelperText error>{getIn(errors, "shipping.address.country")}</FormHelperText>
                                  </FormControl>
                                </Grid>
                              </>
                              : null
                          }

                          {/* Shipping Contact */}

                          <Grid item xs={12}></Grid>

                          <Grid item xs={12} sm={6} md={6} lg={4}>
                            <TextField disabled={readonly} id="shipping.firstName" name="shipping.firstName" value={values.shipping.firstName} label={"First Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "shipping.firstName"))} helperText={getIn(errors, "shipping.firstName")} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={6} lg={4}>
                            <TextField disabled={readonly} id="shipping.lastName" name="shipping.lastName" value={values.shipping.lastName} label={"Last Name"} fullWidth
                              onChange={(e) => {
                                handleChange(e);
                                setTimeout(() => {
                                  cleanPhoneNumber(e.target.value, "shipping");
                                }, 1000);
                              }}
                              onBlur={handleBlur} error={Boolean(getIn(errors, "shipping.lastName"))} helperText={getIn(errors, "shipping.lastName")} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={6} lg={4}>
                            <TextField disabled={readonly} id="shipping.phone" name="shipping.phone" value={values.shipping.phone} label={"Phone (Preferred Mobile#)"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "shipping.phone"))} helperText={getIn(errors, "shipping.phone")} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={6} lg={8}>
                            <TextField disabled={readonly} id="shipping.email" name="shipping.email" value={values.shipping.email} label={"Email"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "shipping.email"))} helperText={getIn(errors, "shipping.email")} />
                          </Grid>
                        </>
                      ) : null}

                      {/* Kit */}
                      {!isGroup && !isParent ? (
                        <>
                          <Grid item xs={12}>
                            <Box p={1}></Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box className={globalStyles.topic}>Kit</Box>
                          </Grid>

                          <Grid item xs={6} sm={6} md={6} lg={4}>
                           <FormControl
  className={globalStyles.formControl}
  fullWidth={true}
  error={Boolean(getIn(errors, "installMethodId"))}
>
  <InputLabel id="state-label" shrink={true}>
    Install Method
  </InputLabel>
  <Select
    disabled={readonly}
    native
    labelId="install-method-id-label"
    id="installMethodId"
    name="installMethodId"
    value={values.installMethodId}
    onChange={handleChange}
    label="Install Method"
    error={Boolean(getIn(errors, "installMethodId"))}
  >
    <option value={0}></option>
    <option value={1}>Pro</option>
    <option value={2}>Self</option>
    <option value={3}>VGI</option>
  </Select>
  {getIn(errors, "installMethodId") && touched.installMethodId && (
    <FormHelperText error>{getIn(errors, "installMethodId")}</FormHelperText>
  )}
</FormControl>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6} lg={4}>
                            <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "kitNo"))}>
                              <InputLabel id="state-label" shrink={true}>
                                Kit
                              </InputLabel>
                              <Select
                                disabled={readonly || values.reviewLocationOtherAttributes.subscriptionBilling}
                                native
                                labelId="kit-no-label"
                                id="kitNo"
                                name="kitNo"
                                value={values.kitNo}
                                onChange={(e) => {
                                  getKitDevices(e.target.value as number);
                                  loadDefaultInstallOption(e.target.value as number);
                                  handleChange(e);
                                  
                                }}
                                label="Kit"
                                error={Boolean(getIn(errors, "kitNo"))}
                              >
                                <option value={0}></option>
                                {
                                  isParent ? <option value={-1}>No Kit</option> : null
                                }
                                {locationKits.map((location, index) => (
                                  <option value={location.kitNo} key={index}>
                                    {location.name}
                                  </option>
                                ))}
                              </Select>

                              <FormHelperText error>{getIn(errors, "kitNo")}</FormHelperText>
                            </FormControl>
                          </Grid>
                          {values.kitNo > 0 ? (
                            <Grid item xs={12}>
                              <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "kitNo"))}>
                                <InputLabel id="state-label" shrink={true}>
                                  Devices
                                </InputLabel>
                              </FormControl>
                              {
                                kitDevices.length > 0 ?
                                  <TableContainer>
                                    <Table>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Description</TableCell>
                                          <TableCell align="right">Quantity</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {kitDevices.length > 0 &&
                                          kitDevices.map((device, index) => (
                                            <TableRow key={index}>
                                              <TableCell component="th" scope="row">
                                                {device.name}
                                              </TableCell>
                                              <TableCell align="right">{device.quantity}</TableCell>
                                            </TableRow>
                                          ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                  : null
                              }


                            </Grid>
                          ) : null}

                          {values.reviewLocationOtherAttributes.subscriptionBilling && values.reviewLocationOtherAttributes.taxExempt ? (
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                              <FormControl fullWidth={true}>
                                <InputLabel>Tax Exempt Attached</InputLabel>
                                <Input
                                  required
                                  disabled={true}
                                  value={values.reviewLocationOtherAttributes.taxExemptFileExists ? "Yes" : "No"}
                                  endAdornment={
                                    <InputAdornment position="end">
                                      {values.reviewLocationOtherAttributes.taxExemptFileExists ? (
                                        <React.Fragment>
                                          <Tooltip arrow title="Download File">
                                            <IconButton
                                              onClick={async () => {
                                                await downloadLocationTaxExemptFile();
                                              }}
                                            >
                                              <GetAppToneIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </React.Fragment>
                                      ) : (
                                        <React.Fragment></React.Fragment>
                                      )}
                                    </InputAdornment>
                                  }
                                />
                              </FormControl>
                            </Grid>
                          ) : null}
                        </>
                      ) : null}
                      {/* Admin Metadata */}

                      {values.adminMetadataValues.length > 0 && !isParent ? (
                        <>
                          <Grid item xs={12}>
                            <Box p={1}></Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box className={globalStyles.topic}>Admin Q&amp;A</Box>
                          </Grid>

                          {values.adminMetadataValues.length > 0 &&
                            values.adminMetadataValues.map((adminMetadataValue, index) => (
                              <Grid item xs={12} md={6} key={index}>
                                <TextField disabled={readonly} id={`adminMetadataValues.${index}.value`} name={`adminMetadataValues.${index}.value`} value={adminMetadataValue.value} label={adminMetadataValue.name} fullWidth onChange={handleChange} onBlur={handleBlur} />
                              </Grid>
                            ))}
                        </>
                      ) : null}

                      {/* Participant Metadata */}

                      {values.participantMetadataValues.length > 0 && !isParent ? (
                        <>
                          <Grid item xs={12}>
                            <Box p={1}></Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box className={globalStyles.topic}>Participant Q&amp;A</Box>
                          </Grid>
                          {values.participantMetadataValues.length > 0 &&
                            values.participantMetadataValues.map((participantMetadataValue, index) => (
                              <Grid item xs={12} md={6} key={index}>
                                <TextField disabled={true} value={participantMetadataValue.value} label={participantMetadataValue.name} fullWidth />
                              </Grid>
                            ))}
                        </>
                      ) : null}

                      {/* Participant Agreement */}

                      {!isGroup && !isParent ? (
                        <>
                          <Grid item xs={12}>
                            <Box p={1}></Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box className={globalStyles.topic}>Participant Agreement</Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3} lg={4}>
                            <TextField disabled={true} label="First Name" value={values.participantAgreement.firstName} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} lg={4}>
                            <TextField disabled={true} label="Last Name" value={values.participantAgreement.lastName} />
                          </Grid>
                          <Grid item xs={12} sm={3} md={3} lg={4}>
                            <TextField disabled={true} label="Signed Date" value={formatDateForDataGrid(values.participantAgreement.date)} />
                          </Grid>
                        </>
                      ) : null}

                      {/* Audit */}
                      <Grid item xs={12}>
                        <Box p={1}></Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box className={globalStyles.topic}>Audit</Box>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3} lg={4}>
                        <TextField disabled={true} label="Last Modified" value={values.lastModifiedBy} />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3} lg={4}>
                        <TextField disabled={true} label="Last Modified Date" value={formatDateTimeForDataGrid(values.lastModifiedDateTime)} />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3} lg={4}>
                        <TextField disabled={true} label="Review Completed" value={formatDateTimeForDataGrid(values.reviewLocationOtherAttributes.reviewCompleted)} />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3} lg={4}>
                        <TextField disabled={true} label="Enrollment Completed" value={formatDateTimeForDataGrid(values.reviewLocationOtherAttributes.enrollCompleted)} />
                      </Grid>
                    </Grid>

                    <Box p={3}></Box>
                  </Form>
                )}
              </Formik>
            </DialogContent>
            <DialogActions>
              <p>&nbsp;</p>
              {canWriteEnlistment(userFunctionAccess) && [1, 2, 3, 4].indexOf(initialFormValues.reviewLocationOtherAttributes.status) >= 0 && initialFormValues.reviewLocationOtherAttributes.subscriptionBilling ? (
                <Button onClick={showCancelOrderReason} className={pageStyles.deleteButton} variant="contained">
                  Cancel Order
                </Button>
              ) : null}

              {canWriteEnlistment(userFunctionAccess) && isGroup ? (
                <Button onClick={showCloseGroupRegistrationReason} className={pageStyles.deleteButton} variant="contained">
                  <u>Close</u>
                </Button>
              ) : null}

              {!readonly && formReference?.current?.values !== null ? (
                <>
                  {!saveButtonVisible && initialFormValues.reviewLocationOtherAttributes.status !== 1 && !initialFormValues.reviewLocationOtherAttributes.isError ? (
                    <>
                      <Button onClick={saveEnroll} className={pageStyles.enrollButton} variant="contained">
                        Enroll
                      </Button>
                    </>
                  ) : null}

                  {!initialFormValues.reviewLocationOtherAttributes.subscriptionBilling ? (
                    <Button onClick={saveOnHold} className={globalStyles.buttonDefault} variant="contained">
                      <Hidden only={["xs"]}>Put On &nbsp;</Hidden>
                      Hold
                    </Button>
                  ) : null}

                  {!initialFormValues.reviewLocationOtherAttributes.subscriptionBilling ? (
                    <Button onClick={showWithdrawReason} className={pageStyles.deleteButton} variant="contained">
                      Withdraw
                    </Button>
                  ) : null}

                  {
                    saveButtonVisible ?
                      <Button disabled={!saveButtonVisible} onClick={submitForm} className={globalStyles.buttonDefault} color="secondary" variant="contained">
                        {initialFormValues.reviewLocationOtherAttributes.status === 1 ?
                          <Hidden only={["xs"]}>Review & &nbsp;</Hidden> : null}
                        Update
                      </Button>
                      : null
                  }
                </>
              ) : null}

              {onHold && !initialFormValues.reviewLocationOtherAttributes.subscriptionBilling ? (
                <Button
                  onClick={() => {
                    saveOnHold();
                  }}
                  className={pageStyles.activateButton}
                  variant="contained"
                >
                  Activate
                </Button>
              ) : null}

              <Button onClick={() => cancel()} className={globalStyles.buttonDefault} autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    enrollButton: {
      backgroundColor: theme.palette.success.main,
      color: "#ffffff",
      marginLeft: "4px",
      marginRight: "4px",

      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
      },
    },
    deleteButton: {
      backgroundColor: theme.palette.error.dark,
      color: "#ffffff",
      marginLeft: "4px",
      marginRight: "4px",

      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
      },
    },

    holdButton: {
      backgroundColor: theme.palette.info.dark,
      color: "#ffffff",
      marginLeft: "4px",
      marginRight: "4px",

      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
      },
    },
    activateButton: {
      backgroundColor: theme.palette.success.main,
      color: "#ffffff",
      marginLeft: "4px",
      marginRight: "4px",

      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
      },
    },
  })
);

export default Review;
